import { useEffect, useMemo, useRef } from 'react';
import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';

import {
  fetchCommentsPage,
  type EngageFeatureType,
  type EngagementComment,
  type EngagementCommentsPage,
} from './engagementComments.api';

/** Stable reference so an empty board doesn't re-trigger renders. */
const EMPTY: EngagementComment[] = [];

type CommentsQueryKey = readonly ['engagementComments', EngageFeatureType, string | undefined];

interface UseEngagementFeatureCommentsArgs {
  engageType: EngageFeatureType;
  featureId?: string;
  /** Gate the query behind the paused/stopped state — nothing to page while live. */
  enabled: boolean;
}

/**
 * Reverse cursor pagination for a paused engagement feature's comment board —
 * the same `useInfiniteQuery` + IntersectionObserver-sentinel shape added for
 * Fantastic Fans (see `useFantasticFans`), just walking backwards: page one is
 * fetched fresh the moment the board is enabled, and scrolling toward the top
 * pages further into the past via `offset`. No polling — the board is static
 * once the interaction has stopped.
 */
export const useEngagementFeatureComments = ({
  engageType,
  featureId,
  enabled,
}: UseEngagementFeatureCommentsArgs) => {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const query = useInfiniteQuery<
    EngagementCommentsPage,
    Error,
    InfiniteData<EngagementCommentsPage, number>,
    CommentsQueryKey,
    number
  >({
    queryKey: ['engagementComments', engageType, featureId],
    queryFn: ({ pageParam }) => fetchCommentsPage(engageType, featureId!, pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, page) => sum + page.comments.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    enabled: enabled && Boolean(featureId),
    // Hard reset per feature — don't restore a previously scrolled board.
    gcTime: 0,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const { data, hasNextPage, isFetchingNextPage, isLoading } = query;

  // Oldest fetched page first, most-recent page last — matches display order.
  const comments = useMemo(() => {
    const pages = data?.pages;
    if (!pages?.length) return EMPTY;
    return [...pages].reverse().flatMap((page) => page.comments);
  }, [data]);

  // The observer closure only re-runs on `hasNextPage`, so read the live query
  // through a ref rather than capturing stale flags.
  const queryRef = useRef(query);
  queryRef.current = query;

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage) return undefined;

    const observer = new IntersectionObserver((entries) => {
      if (!entries[0]?.isIntersecting) return;
      const current = queryRef.current;
      if (!current.hasNextPage || current.isFetching || current.isFetchingNextPage) return;
      void current.fetchNextPage({ cancelRefetch: false });
    }, { rootMargin: '200px' });

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage]);

  return {
    comments,
    sentinelRef,
    hasMore: Boolean(hasNextPage),
    isLoading,
    isLoadingMore: isFetchingNextPage,
  } as const;
};
